<template>
  <div class="container mt-2">
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status"></div>
      <p>{{ $t("qbank.quiz.loading_question") }}</p>
    </div>
    <div v-else-if="noMoreQuestions" class="text-center">
      <!-- عرض رسالة عند عدم وجود أسئلة -->
      <p class="alert alert-warning">
        {{ $t("qbank.quiz.no_more_questions") }}
      </p>
    </div>
    <div v-else>
      <div class="card shadow-lg p-3 question-box">
        <!-- Question Header Bar -->
        <div class="question-header-bar">
          <!-- Question Number & Progress -->
          <div class="question-progress-section">
            <span class="dropdown">
              <button
                class="question-number-btn dropdown-toggle"
                type="button"
                id="dropdownMenuButton1"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="bi bi-list-ol"></i>
                <span class="question-num">{{ questionNo }}</span>
                <span class="question-separator">/</span>
                <span class="question-total">{{ lastQuestion }}</span>
              </button>
              <ul
                class="dropdown-menu questions-dropdown"
                aria-labelledby="dropdownMenuButton1"
              >
                <li
                  v-for="i in Array.from(
                    { length: lastQuestion },
                    (_, index) => index + 1
                  )"
                  :key="i"
                >
                  <span
                    class="dropdown-item"
                    :class="{ 'current-question': i === questionNo }"
                    @click="goToQuestion(i)"
                  >
                    <span class="question-status-icon">
                      <i
                        v-if="correct_questions.includes(i)"
                        class="bi bi-check-circle-fill text-success"
                      ></i>
                      <i
                        v-else-if="incorrect_questions.includes(i)"
                        class="bi bi-x-circle-fill text-danger"
                      ></i>
                      <i
                        v-else-if="!unanswered_questions.includes(i)"
                        class="bi bi-circle-fill answered-dot"
                      ></i>
                      <i v-else class="bi bi-circle text-muted"></i>
                    </span>
                    <span class="question-item-num"
                      >{{ $t("question") }} {{ i }} {{ $t("of") }}
                      {{ lastQuestion }}</span
                    >
                  </span>
                </li>
              </ul>
            </span>
          </div>

          <!-- Action Buttons -->
          <div class="question-actions">
            <!-- Exit Button (for Mia users - confirms then goes to Mia) -->
            <button
              v-if="fromMia"
              class="action-btn exit-btn"
              @click="confirmExitToMia"
              :title="$t('qbank.quiz.exit') || 'Exit'"
            >
              <span>{{ $t("qbank.quiz.exit") || "Exit" }}</span>
            </button>

            <!-- End Quiz Button (for regular Qbank users) -->
            <button
              v-else
              class="action-btn end-quiz-btn"
              @click="confirmEndQuiz"
              :title="$t('qbank.quiz.end_quiz')"
            >
              <span>{{ $t("qbank.quiz.end_quiz") }}</span>
            </button>

            <!-- Bookmark Button -->
            <button
              class="action-btn bookmark-btn"
              :class="{ 'is-bookmarked': question.is_favorite }"
              @click="toggleFavorite(question)"
              :title="
                question.is_favorite
                  ? $t('qbank.quiz.remove_bookmark')
                  : $t('qbank.quiz.add_bookmark')
              "
            >
              <i
                class="bi"
                :class="
                  question.is_favorite ? 'bi-bookmark-fill' : 'bi-bookmark'
                "
              ></i>
            </button>

            <!-- AI Help Button -->
            <button
              class="action-btn ai-help-btn"
              data-bs-toggle="modal"
              @click.prevent="aiHelp(this.qbankId, this.question.id)"
              data-bs-target="#help-modal"
              :title="$t('qbank.quiz.teach_me')"
            >
              <i class="bi bi-lightbulb"></i>
            </button>

            <!-- More Options Dropdown -->
            <span class="dropdown">
              <button
                class="action-btn more-btn dropdown-toggle"
                type="button"
                id="moreOptionsDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                :title="$t('qbank.quiz.options')"
              >
                <i class="bi bi-three-dots-vertical"></i>
              </button>
              <ul
                class="dropdown-menu dropdown-menu-end"
                aria-labelledby="moreOptionsDropdown"
              >
                <li>
                  <span
                    class="dropdown-item"
                    data-bs-toggle="modal"
                    @click.prevent="showQuestionDetails()"
                    data-bs-target="#question-details-modal"
                  >
                    <i class="bi bi-info-circle me-2"></i>
                    {{ $t("qbank.quiz.question_details") || "تفاصيل السؤال" }}
                  </span>
                </li>
                <li>
                  <span
                    class="dropdown-item"
                    data-bs-toggle="modal"
                    @click.prevent="addReport(this.qbankId, this.question.id)"
                    data-bs-target="#add-report-modal"
                  >
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    {{
                      $t("qbank.quiz.send_report_to_admin") ||
                      "إرسال بلاغ للإدارة"
                    }}
                  </span>
                </li>
              </ul>
            </span>
          </div>
        </div>

        <!-- Add Private Note Button -->
        <div class="add-note-container">
          <button
            class="add-note-bar-btn"
            data-bs-toggle="modal"
            @click.prevent="addNote(this.qbankId, this.question.id)"
            data-bs-target="#add-note-modal"
          >
            <i class="bi bi-pencil-square"></i>
            <span>{{ $t("qbank.quiz.add_private_notes") }}</span>
          </button>
        </div>

        <!-- Question Content -->
        <h5
          class="content-math card-title mb-4"
          v-html="cleanedContent(question.content)"
          :dir="getDirection(question?.content)"
        ></h5>
        <div v-if="question.file" style="text-align: center">
          <template v-if="isImage(question.file)">
            <a :href="question.file" target="_blank">
              <img
                style="max-height: 410px"
                :src="question.file"
                alt="Question Image"
                class="img-fluid mb-3"
              />
            </a>
          </template>
          <template v-else>
            <a :href="question.file" target="_blank" class="btn btn-link"
              >📁 View File</a
            >
          </template>
        </div>
        <div :dir="getDirection(question?.content)">
          <div v-if="question.type === 3" class="text-question">
            <!-- Text Question -->
            <textarea
              v-if="!question.is_answered"
              v-model="userAnswer"
              class="form-control mb-3"
              :readonly="question.is_answered"
              style="min-height: 150px"
              rows="9"
              :placeholder="$t('qbank.quiz.write_answer_here')"
            ></textarea>

            <div
              v-if="question.is_answered"
              class="mt-3 p-3 border rounded bg-light"
            >
              <!-- Display User Answer -->
              <div
                v-if="question.is_correct == 1"
                class="alert alert-success p-2"
              >
                {{ getTranslation(question?.content).correct }}
              </div>
              <div v-else class="alert alert-danger p-2">
                {{ getTranslation(question?.content).incorrect }}
              </div>

              <p>
                <strong>{{ $t("qbank.quiz.your_answer") }}:</strong>
              </p>
              <div class="alert alert-dark p-2 mb-3">
                {{ question.user_answer }}
              </div>
              <p>
                <strong>{{ $t("qbank.quiz.correct_answer") }}:</strong>
              </p>
              <div class="alert alert-primary p-2 mb-3">
                {{ question.correct_answer }}
              </div>
              <!-- Correctness Feedback -->
            </div>
          </div>

          <div
            v-else-if="question.type === 1"
            class="multi-choose p-3 border rounded bg-light"
          >
            <!-- ✅ نتيجة الإجابة في الأعلى -->
            <b v-if="question.is_answered">
              <span
                v-if="
                  question.choices.some(
                    (c) => c.is_correct && c.id == question.user_answer
                  )
                "
                class="text-success"
              >
                {{ getTranslation(question?.content).correct }}
              </span>
              <span v-else class="text-danger">
                {{ getTranslation(question?.content).incorrect }}
              </span>
            </b>
            <br />
            <!-- الخيارات -->

            <div
              v-for="choice in question.choices"
              :key="choice.id"
              class="form-check mb-3 d-flex align-items-center"
              :class="{
                'bg-success bg-opacity-25 rounded':
                  choice.is_correct && question.user_answer == choice.id,
                'bg-danger bg-opacity-25 rounded':
                  question.is_answered &&
                  choice.id == question.user_answer &&
                  !choice.is_correct,
              }"
            >
              <label
                class="form-check-label flex-grow-1"
                :class="{
                  'text-success fw-bold':
                    question.is_answered && choice.is_correct,
                  'text-danger fw-bold':
                    question.is_answered &&
                    choice.id == question.user_answer &&
                    !choice.is_correct,
                }"
              >
                <input
                  v-if="!question.is_answered"
                  type="radio"
                  :id="`choice-${choice.id}`"
                  :value="choice.id"
                  v-model="userAnswer"
                  class="form-check-input me-2"
                />

                <!-- الأيقونات بدلاً من الدوائر -->
                <i
                  v-if="question.is_answered && choice.is_correct"
                  class="fa fa-check-circle me-2 text-success"
                ></i>
                <i
                  v-else-if="
                    question.is_answered && choice.id == question.user_answer
                  "
                  class="fa fa-times-circle me-2 text-danger"
                ></i>
                <i
                  v-else-if="question.is_answered"
                  class="fa fa-circle me-2 text-secondary"
                ></i>

                <span
                  class="content-math"
                  v-html="cleanedContent(choice.content)"
                ></span>
              </label>
            </div>
          </div>

          <div
            v-else-if="question.type === 2"
            class="true-false-question p-3 border rounded bg-light"
            style="margin: 0 auto; width: 200px"
          >
            <b v-if="question.is_answered">
              <span
                v-if="
                  question.choices.some(
                    (c) => c.is_correct && c.id == question.user_answer
                  )
                "
                class="text-success"
              >
                {{ getTranslation(question?.content).correct }}
              </span>
              <span v-else class="text-danger">
                {{ getTranslation(question?.content).incorrect }}
              </span>
            </b>
            <br /><br />
            <div
              style="border: 1px solid #ddd; padding: 5px; border-radius: 10px"
              v-for="choice in question.choices"
              :key="choice.id"
              class="form-check mb-3 d-flex align-items-center"
              :class="{
                'bg-success bg-opacity-25 rounded':
                  choice.is_correct && question.user_answer == choice.id,
                'bg-danger bg-opacity-25 rounded':
                  question.is_answered &&
                  choice.id == question.user_answer &&
                  !choice.is_correct,
              }"
            >
              <label
                class="form-check-label flex-grow-1"
                :class="{
                  'text-success fw-bold':
                    question.is_answered && choice.is_correct,
                  'text-danger fw-bold':
                    question.is_answered &&
                    choice.id == question.user_answer &&
                    !choice.is_correct,
                }"
              >
                <input
                  v-if="!question.is_answered"
                  type="radio"
                  :id="`choice-${choice.id}`"
                  :value="choice.id"
                  v-model="userAnswer"
                  class="form-check-input me-2"
                />

                <!-- الأيقونات بدلاً من الدوائر -->
                <i
                  v-if="question.is_answered && choice.is_correct"
                  class="fa fa-check-circle me-2 text-success"
                ></i>
                <i
                  v-else-if="
                    question.is_answered && choice.id == question.user_answer
                  "
                  class="fa fa-times-circle me-2 text-danger"
                ></i>
                <i
                  v-else-if="question.is_answered"
                  class="fa fa-circle me-2 text-secondary"
                ></i>

                <!-- التسمية -->
                &nbsp;&nbsp; {{ choice.content }}
              </label>
            </div>
          </div>

          <div v-else-if="question.type === 4">
            <!-- Matching Question -->
            <div class="row">
              <div
                v-if="question.is_correct == 1"
                class="m-1 alert alert-success text-center"
              >
                {{ $t("qbank.quiz.correct_answer_message") }}
              </div>
              <div
                v-else-if="question.is_correct == 0"
                class="m-1 alert alert-danger text-center"
              >
                {{ $t("qbank.quiz.incorrect_answer_message") }}
              </div>
              <div class="col-md-6">
                <h5 class="">{{ $t("qbank.quiz.left_items") }}</h5>
                <ul class="list-group">
                  <li
                    v-for="item in question.matching_options.left"
                    :key="item.id"
                    class="list-group-item"
                  >
                    <span
                      class="content-math"
                      v-html="cleanedContent(item.content)"
                    ></span>
                  </li>
                </ul>
              </div>
              <div v-if="question.is_answered" class="col-md-6">
                <h5 class="">{{ $t("qbank.quiz.right_items") }}</h5>
                <ul
                  class="list-group"
                  :class="
                    question.is_correct == 1 ? 'bg-success' : 'bg-secondary'
                  "
                >
                  <li
                    v-for="item in question.matching_options.right"
                    :key="item.id"
                    class="list-group-item"
                  >
                    <span
                      class="content-math"
                      v-html="cleanedContent(item.content)"
                    ></span>
                  </li>
                </ul>
              </div>
              <div v-if="!question.is_answered" class="col-md-6">
                <h5 class="">{{ $t("qbank.quiz.right_items") }}</h5>
                <ul class="list-group" ref="sortableRight">
                  <li
                    v-for="item in question.matching_options.right"
                    :key="item.id"
                    class="list-group-item"
                  >
                    <span
                      class="content-math"
                      v-html="cleanedContent(item.content)"
                    ></span>
                  </li>
                </ul>
              </div>
              <hr class="text-primary mt-5" />
              <h3 v-if="question.is_answered" class="text-primary mt-2">
                {{ $t("qbank.quiz.correct_answer") }}
              </h3>
              <div v-if="question.is_answered" class="col-md-6">
                <h5 class="">{{ $t("qbank.quiz.left_items") }}</h5>
                <ul class="list-group">
                  <li
                    v-for="item in question.correct_matching_pairs.left"
                    :key="item.id"
                    class="list-group-item"
                  >
                    <span
                      class="content-math"
                      v-html="cleanedContent(item.content)"
                    ></span>
                  </li>
                </ul>
              </div>
              <div v-if="question.is_answered" class="col-md-6">
                <h5 class="">{{ $t("qbank.quiz.right_items") }}</h5>
                <ul class="list-group" ref="sortableRight">
                  <li
                    v-for="item in question.correct_matching_pairs.right"
                    :key="item.id"
                    class="list-group-item"
                  >
                    <span
                      class="content-math"
                      v-html="cleanedContent(item.content)"
                    ></span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <h6
            style="text-align: center; font-weight: 600; font-size: 12px"
            :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
          >
            {{ $t("qbank.quiz.correct_answer_percentage") }}
            <span
              :class="
                question.correct_answers_percentage < 50
                  ? 'text-danger'
                  : 'text-success'
              "
              style="font-weight: 700"
            >
              {{ question.correct_answers_percentage }} %
            </span>
          </h6>
          <div
            v-if="question.is_answered && question.explanation"
            class="explanation-card mt-4"
          >
            <div class="explanation-header">
              <div class="explanation-icon">
                <i class="fas fa-lightbulb"></i>
              </div>
              <h6 class="explanation-title">
                {{ $t("qbank.quiz.explanation") }}
              </h6>
            </div>
            <div class="explanation-content">
              <div
                class="content-math explanation-text"
                v-html="cleanedContent(question.explanation)"
              ></div>
            </div>

            <!-- Explanation File/Image -->
            <div v-if="question.explanation_file" class="explanation-file mt-3">
              <template v-if="isImage(question.explanation_file)">
                <img
                  :src="question.explanation_file"
                  alt="Explanation Image"
                  class="img-fluid explanation-image"
                />
              </template>
              <template v-else>
                <a
                  :href="question.explanation_file"
                  target="_blank"
                  class="explanation-file-link"
                >
                  <i class="fas fa-file-alt me-2"></i>
                  {{
                    $t("qbank.quiz.view_explanation_file") ||
                    "View Explanation File"
                  }}
                </a>
              </template>
            </div>
          </div>

          <!-- Standalone Explanation File (when no text explanation) -->
          <div
            v-else-if="question.is_answered && question.explanation_file"
            class="explanation-card mt-4"
          >
            <div class="explanation-header">
              <div class="explanation-icon">
                <i class="fas fa-lightbulb"></i>
              </div>
              <h6 class="explanation-title">
                {{ $t("qbank.quiz.explanation") }}
              </h6>
            </div>
            <div class="explanation-content">
              <template v-if="isImage(question.explanation_file)">
                <img
                  :src="question.explanation_file"
                  alt="Explanation Image"
                  class="img-fluid explanation-image"
                />
              </template>
              <template v-else>
                <a
                  :href="question.explanation_file"
                  target="_blank"
                  class="explanation-file-link"
                >
                  <i class="fas fa-file-alt me-2"></i>
                  {{
                    $t("qbank.quiz.view_explanation_file") ||
                    "View Explanation File"
                  }}
                </a>
              </template>
            </div>
          </div>
        </div>
        <!-- Render question based on type -->

        <!-- Navigation Buttons -->
        <div class="navigation-bar">
          <button class="nav-btn nav-btn-prev" @click="previousQuestion">
            <i
              class="bi"
              :class="
                $i18n.locale === 'ar' ? 'bi-chevron-right' : 'bi-chevron-left'
              "
            ></i>
            <span>{{ $t("qbank.quiz.previous") }}</span>
          </button>

          <!-- Single button: Skip or Next depending on answer selection -->
          <transition name="btn-fade" mode="out-in">
            <!-- Case 1: Last question - Show End Exam -->
            <button
              v-if="questionNo >= lastQuestion"
              key="end-exam"
              class="nav-btn nav-btn-end-exam"
              @click="fromMia ? completeQuizForMia() : confirmEndQuiz()"
            >
              <i class="bi bi-flag-fill"></i>
              <span>{{ $t("qbank.quiz.end_exam") }}</span>
            </button>
            
            <!-- Case 2: Answer revealed OR has selected answer - Show Next -->
            <button
              v-else-if="answerRevealed || question.is_answered || hasSelectedAnswer"
              key="next"
              class="nav-btn nav-btn-next"
              @click="nextQuestion"
            >
              <span>{{ $t("qbank.quiz.next") }}</span>
              <i class="bi" :class="$i18n.locale === 'ar' ? 'bi-chevron-left' : 'bi-chevron-right'"></i>
            </button>
            
            <!-- Case 3: No answer selected - Show Skip -->
            <button
              v-else
              key="skip"
              class="nav-btn nav-btn-skip"
              @click="skipQuestion"
            >
              <span>{{ $t("qbank.quiz.skip") }}</span>
              <i class="bi" :class="$i18n.locale === 'ar' ? 'bi-skip-backward' : 'bi-skip-forward'"></i>
            </button>
          </transition>
        </div>
      </div>
    </div>
  </div>
  <AddReport />
  <AddNote />
  <Notes ref="notesRef" />
  <HelpAi ref="aiRef" />

  <!-- Question Details Modal -->
  <div class="modal fade" id="question-details-modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content question-details-modal">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-info-circle-fill me-2"></i>
            {{ $t("qbank.quiz.details") }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>
        <div class="modal-body" v-if="question">
          <div class="details-list">
            <!-- Lesson -->
            <div class="detail-row" v-if="question.lesson">
              <div class="detail-icon lesson">
                <i class="fas fa-book"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{ $t("qbank.quiz.lesson") }}</span>
                <span class="detail-value">{{ question.lesson.title }}</span>
              </div>
            </div>

            <!-- Category -->
            <div class="detail-row" v-if="question.child_category">
              <div class="detail-icon category">
                <i class="fas fa-folder"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.category")
                }}</span>
                <span class="detail-value">{{
                  question.child_category.name
                }}</span>
              </div>
            </div>

            <!-- Source -->
            <div class="detail-row" v-if="question.source">
              <div class="detail-icon source">
                <i class="fas fa-file-alt"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{ $t("qbank.quiz.source") }}</span>
                <span class="detail-value"
                  >{{ question.source.name }}
                  <span v-if="question.source.year"
                    >({{ question.source.year }})</span
                  ></span
                >
              </div>
            </div>

            <!-- Difficulty -->
            <div class="detail-row">
              <div class="detail-icon difficulty">
                <i class="fas fa-signal"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.difficulty")
                }}</span>
                <span class="detail-value">
                  <span
                    class="difficulty-badge"
                    :class="getDifficultyClass(question.difficulty)"
                  >
                    {{ getDifficultyText(question.difficulty) }}
                  </span>
                </span>
              </div>
            </div>

            <!-- Estimated Time -->
            <div class="detail-row" v-if="question.estimated_time">
              <div class="detail-icon time">
                <i class="fas fa-clock"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.estimated_time")
                }}</span>
                <span class="detail-value"
                  >{{ Math.round(question.estimated_time / 60) }}
                  {{ $t("qbank.quiz.minutes") }}</span
                >
              </div>
            </div>

            <!-- Correct Answers Percentage -->
            <div
              class="detail-row"
              v-if="question.correct_answers_percentage !== undefined"
            >
              <div class="detail-icon percentage">
                <i class="fas fa-chart-pie"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.correct_percentage")
                }}</span>
                <span class="detail-value percentage-value"
                  >{{ question.correct_answers_percentage }}%</span
                >
              </div>
            </div>

            <!-- Question Type -->
            <div class="detail-row">
              <div class="detail-icon type">
                <i class="fas fa-question-circle"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.question_type")
                }}</span>
                <span class="detail-value">{{
                  getQuestionTypeText(question.type)
                }}</span>
              </div>
            </div>

            <!-- Your Reports -->
            <div class="detail-row">
              <div class="detail-icon reports">
                <i class="fas fa-flag"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.your_reports")
                }}</span>
                <div class="detail-value-row">
                  <span
                    class="detail-value"
                    v-if="question.user_reports_count > 0"
                  >
                    {{ question.user_reports_count }}
                  </span>
                  <span class="detail-value text-muted" v-else>
                    {{ $t("qbank.quiz.no_reports") }}
                  </span>
                  <button
                    v-if="question.user_reports_count > 0"
                    class="btn-view-details"
                    data-bs-dismiss="modal"
                    @click="viewReports"
                  >
                    <i class="fas fa-eye"></i>
                    {{ $t("qbank.quiz.view_reports") || "عرض البلاغات" }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Your Notes -->
            <div class="detail-row">
              <div class="detail-icon notes">
                <i class="fas fa-sticky-note"></i>
              </div>
              <div class="detail-content">
                <span class="detail-label">{{
                  $t("qbank.quiz.your_notes")
                }}</span>
                <div class="detail-value-row">
                  <span
                    class="detail-value"
                    v-if="question.user_notes_count > 0"
                  >
                    {{ question.user_notes_count }}
                  </span>
                  <span class="detail-value text-muted" v-else>
                    {{ $t("qbank.quiz.no_notes") }}
                  </span>
                  <button
                    v-if="question.user_notes_count > 0"
                    class="btn-view-details"
                    data-bs-dismiss="modal"
                    @click="viewNotes"
                  >
                    <i class="fas fa-eye"></i>
                    {{ $t("qbank.quiz.view_notes") || "عرض الملاحظات" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";
import Sortable from "sortablejs";
import Swal from "sweetalert2";
import AddReport from "../AddReport.vue";
import AddNote from "../AddNote.vue";
import Notes from "../Notes.vue";
import HelpAi from "../HelpAi.vue";

import { favoriteQuestion } from "@/utils/questionService";

export default {
  components: {
    AddReport,
    AddNote,
    Notes,
    HelpAi,
  },
  data() {
    return {
      token: localStorage.getItem("access_token"),
      qbankId: this.$route.query.qref,
      lastQuestion: this.$route.query.total,
      quizMode: this.$route.query.mode,
      questionNo: Number(this.$route.query.num) || 1,
      subscriptionId: this.$route.query.subscriptionId,
      fromMia: String(this.$route.query.fromMia) === "1",
      question: null,
      answerRevealed: false,
      userAnswer: null,
      unanswered_questions: [],
      correct_questions: [],
      incorrect_questions: [],
      loading: true,
      noMoreQuestions: false,
      hasSelectedAnswer: false,
    };
  },

  watch: {
    noMoreQuestions(newVal) {
      if (newVal) {
        // For Mia users, go to summary page with fromMia flag
        if (this.fromMia) {
          this.completeQuizForMia();
        } else {
          // Automatically redirect to Performance Analysis page when test is completed
          this.completeQuizAndRedirect();
        }
      }
    },
    question: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderMath();
          }, 100);
        });
      },
      deep: true,
    },
    userAnswer(newVal) {
      // Track when user selects an answer (for MCQ and True/False)
      // This changes the button from "Skip" to "Next"
      if (
        !this.answerRevealed &&
        !this.question.is_answered &&
        newVal &&
        (this.question.type === 1 || this.question.type === 2)
      ) {
        this.hasSelectedAnswer = true;
      }
    },
  },

  updated() {
    this.renderMath();
  },
  methods: {
    cleanedContent(rawResponse) {
      if (!rawResponse) return "";
      return rawResponse
        .replace(/\\\\/g, "\\")
        .replace(/\\ar\b/g, "")
        .replace(/\\ar/g, "")
        .replace(/\$\\ar\$/g, "")
        .replace(/\$ar\$/g, "")
        .replace(/\\\[/g, "$$")
        .replace(/\\\]/g, "$$")
        .replace(/\\\(/g, "$")
        .replace(/\\\)/g, "$");
    },
    renderMath() {
      if (window.MathJax && window.MathJax.typesetPromise) {
        this.$nextTick(() => {
          setTimeout(() => {
            const elems = document.querySelectorAll(".content-math");
            const elemsArray = Array.from(elems);
            elemsArray.forEach((el) => {
              if (el.innerHTML.includes("📐")) {
                el.innerHTML = el.innerHTML.replace("📐", "");
              }
              // Remove \ar from content
              el.innerHTML = el.innerHTML
                .replace(/\\ar\b/g, "")
                .replace(/\\ar/g, "")
                .replace(/\$\\ar\$/g, "")
                .replace(/\$ar\$/g, "")
                .replace(/ar(?=\s*\$)/g, "");
            });
            if (elemsArray.length > 0) {
              MathJax.typesetClear(elemsArray);
              MathJax.typesetPromise(elemsArray)
                .then(() => {
                  // Clean up any remaining \ar after MathJax processing
                  elemsArray.forEach((el) => {
                    el.innerHTML = el.innerHTML
                      .replace(/\\ar/g, "")
                      .replace(/\bar\b/g, "");
                  });
                })
                .catch((err) => console.log("MathJax error:", err));
            }
          }, 200);
        });
      }
    },
    async addNote(qbankNoteId, questionNoteId) {
      document.getElementById("qbankNoteId").value = qbankNoteId;
      document.getElementById("questionNoteId").value = questionNoteId;
    },
    isImage(file) {
      return /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(file);
    },
    async aiHelp(qbankHelpId, questionHelpId) {
      document.getElementById("questionHelpId").value = questionHelpId;
      this.$refs.aiRef.startExplanation();
    },
    async addReport(qbankReportId, questionReportId) {
      document.getElementById("qbankReportId").value = qbankReportId;
      document.getElementById("questionReportId").value = questionReportId;
    },
    async showNotes(qbankNoteId, questionNoteId) {
      document.getElementById("qbankNoteId").value = qbankNoteId;
      document.getElementById("questionNoteId").value = questionNoteId;
      this.$refs.notesRef.showNotes();
    },
    async showQuestionDetails() {
      // Fetch notes and reports counts for the current question
      try {
        const [notesResponse, reportsResponse] = await Promise.all([
          fetch(
            `${apiUrl}/qbanks/${this.qbankId}/questions/${this.question.id}/notes`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${this.token}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `${apiUrl}/qbanks/${this.qbankId}/questions/${this.question.id}/reports`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${this.token}`,
                "Content-Type": "application/json",
              },
            }
          ),
        ]);

        if (notesResponse.ok) {
          const notesData = await notesResponse.json();
          this.question.user_notes_count = notesData.data?.length || 0;
        }

        if (reportsResponse.ok) {
          const reportsData = await reportsResponse.json();
          this.question.user_reports_count = reportsData.data?.length || 0;
        }
      } catch (error) {
        console.error("Error fetching question details:", error);
      }
    },
    viewNotes() {
      this.$nextTick(() => {
        this.showNotes(this.qbankId, this.question.id);
        const notesModal = new bootstrap.Modal(
          document.getElementById("show-note-modal")
        );
        notesModal.show();
      });
    },
    viewReports() {
      this.$nextTick(() => {
        this.showReportsModal(this.qbankId, this.question.id);
      });
    },
    confirmEndQuiz() {
      // Get unanswered questions list
      const unansweredList = this.unanswered_questions || [];
      const unansweredCount = unansweredList.length;

      let htmlContent = "";
      if (unansweredCount > 0) {
        htmlContent = `
          <div style="text-align: center;">
            <p style="margin: 0 0 10px; color: #92400e; font-weight: 600; font-size: 0.95rem;">
              ${
                this.$t("qbank.quiz.unanswered_count_message")?.replace(
                  "{count}",
                  unansweredCount
                ) || `عدد الأسئلة غير المجابة: ${unansweredCount}`
              }
            </p>
            <div style="display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; margin: 10px 0;">
              ${unansweredList
                .slice(0, 15)
                .map(
                  (q) =>
                    `<span style="background: #fee2e2; color: #dc2626; padding: 4px 10px; border-radius: 6px; font-weight: 600; font-size: 0.85rem;">${q}</span>`
                )
                .join("")}
              ${
                unansweredList.length > 15
                  ? `<span style="color: #64748b; font-size: 0.85rem;">...</span>`
                  : ""
              }
            </div>
          </div>
        `;
      } else {
        htmlContent = `
          <p style="margin: 0; color: #166534; font-weight: 600; font-size: 0.95rem;">
            ${
              this.$t("qbank.quiz.all_answered") ||
              "أحسنت! لقد أجبت على جميع الأسئلة"
            }
          </p>
        `;
      }

      Swal.fire({
        title:
          this.$t("qbank.quiz.end_quiz_confirm") ||
          "هل أنت متأكد من إنهاء الاختبار؟",
        html: htmlContent,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        confirmButtonText:
          this.$t("qbank.quiz.yes_end_quiz") || "نعم، إنهاء الاختبار",
        cancelButtonText:
          this.$t("qbank.quiz.continue_quiz") || "متابعة الاختبار",
        reverseButtons: true,
        width: "450px",
        padding: "20px",
      }).then((result) => {
        if (result.isConfirmed) {
          this.endQuiz();
        }
      });
    },
    async endQuiz() {
      // Complete quiz first, then navigate to performance page
      await this.completeQuiz();
    },
    async showReportsModal(qbankId, questionId) {
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${qbankId}/questions/${questionId}/reports`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const reports = data.data || [];
          if (reports.length > 0) {
            const reportsHtml = reports
              .map(
                (report) => `
              <div style="border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; margin-bottom: 10px; text-align: right;">
                <strong style="color: #00c444;">${report.title || ""}</strong>
                <p style="margin: 8px 0 4px; color: #475569;">${
                  report.content || ""
                }</p>
                <small style="color: #94a3b8;">${new Date(
                  report.created_at
                ).toLocaleString()}</small>
              </div>
            `
              )
              .join("");

            Swal.fire({
              title: this.$t("qbank.quiz.your_reports") || "بلاغاتك",
              html: `<div style="max-height: 300px; overflow-y: auto;">${reportsHtml}</div>`,
              confirmButtonText: this.$t("common.close") || "إغلاق",
              confirmButtonColor: "#00c444",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
      }
    },
    getDifficultyClass(difficulty) {
      const classes = {
        1: "difficulty-easy",
        2: "difficulty-medium",
        3: "difficulty-hard",
      };
      return classes[difficulty] || "difficulty-medium";
    },
    getDifficultyText(difficulty) {
      const texts = {
        1: this.$t("qbank.quiz.difficulty_easy"),
        2: this.$t("qbank.quiz.difficulty_medium"),
        3: this.$t("qbank.quiz.difficulty_hard"),
      };
      return texts[difficulty] || texts[2];
    },
    getQuestionTypeText(type) {
      const types = {
        1: this.$t("qbank.quiz.type_multiple_choice"),
        2: this.$t("qbank.quiz.type_true_false"),
        3: this.$t("qbank.quiz.type_text"),
        4: this.$t("qbank.quiz.type_matching"),
      };
      return types[type] || types[1];
    },
    getDirection(text) {
      if (!text) return "ltr";
      // إزالة الرموز والعلامات
      const cleanText = text.replace(/[:;.,/\()؟?!@#$%^&*[\]{}<>|_+=~`]/g, "");
      // التحقق من وجود أحرف عربية أو عبرية أو فارسية
      const rtlRegex = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
      return rtlRegex.test(cleanText) ? "rtl" : "ltr";
    },
    getTranslation(content) {
      return this.getDirection(content) === "rtl"
        ? { correct: "✔ إجابتك صحيحة", incorrect: "✘ إجابتك خاطئة" }
        : {
            correct: "✔ Your answer is correct",
            incorrect: "✘ Your answer is incorrect",
          };
    },
    async toggleFavorite(question) {
      try {
        favoriteQuestion(question.id);
        // عكس الحالة في الواجهة مباشرة
        question.is_favorite = !question.is_favorite;

        alertify.success(
          question.is_favorite
            ? this.$t("qbank.quiz.added_to_favorites")
            : this.$t("qbank.quiz.removed_from_favorites")
        );
      } catch (err) {
        console.error(err);
        alertify.error(this.$t("qbank.quiz.error_try_later"));
      }
    },
    favoriteQuestion,

    async fetchQuestion() {
      this.loading = true;
      this.noMoreQuestions = false; // إعادة تعيين الحالة عند كل طلب
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/questions?number=${this.questionNo}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (!data.question) {
            throw new Error("No more questions");
          }
          this.question = data.question;
          this.unanswered_questions = data.unanswered_questions;
          this.correct_questions = data.correct_questions || [];
          this.incorrect_questions = data.incorrect_questions || [];

          this.userAnswer = null;

          // Initialize SortableJS for matching questions
          if (
            this.question.type === 4 &&
            this.question.matching_options?.right
          ) {
            this.$nextTick(() => {
              setTimeout(() => {
                this.initSortable();
              }, 2000);
            });
          }

          // Explicitly render math after fetching question to ensure proper formatting
          this.$nextTick(() => {
            this.renderMath();
          });
        } else if (response.status === 404) {
          this.noMoreQuestions = true; // تعيين الحالة عند عدم وجود أسئلة
        } else {
          console.error("فشل جلب الأسئلة");
        }
      } catch (error) {
        console.error("خطأ في جلب الأسئلة:", error);
        this.noMoreQuestions = true; // عرض رسالة عند أي خطأ
      } finally {
        this.loading = false;
      }
    },
    async skipQuestion() {
      if (this.questionNo == this.lastQuestion) {
        this.nextQuestion();
        return;
      }
      // إعادة تعيين الحالة، والانتقال للسؤال التالي
      this.answerRevealed = false;
      this.hasSelectedAnswer = false;
      this.questionNo += 1;
      this.updateUrl();
      this.fetchQuestion();
      this.destroySortable();
    },
    async nextQuestion() {
      // تحقق إذا كنا في وضع "tutor"
      // لو الإجابة لم تُعرض بعد
      if (!this.answerRevealed && !this.question.is_answered) {
        await this.submitAnswer();
        this.answerRevealed = true; // خليها true عشان المرة الجاية ينتقل للسؤال التالي
        // إعادة تعيين الحالة، والانتقال للسؤال التالي

        this.updateUrl();
        this.fetchQuestion();
        this.destroySortable();
      } else {
        if (this.questionNo == this.lastQuestion) {
          // For Mia users, go to summary page with fromMia flag
          if (this.fromMia) {
            await this.completeQuizForMia();
            return;
          }

          const unansweredList = this.unanswered_questions;
          let unansweredMessage = "";

          if (unansweredList.length > 0) {
            if (unansweredList.length > 10) {
              unansweredMessage = `لديك أكثر من 10 أسئلة غير مجابة`;
            } else {
              unansweredMessage = ` لم تجب على هذه الأسئلة بعد    : ${unansweredList.join(
                ", "
              )}`;
            }
          } else {
            unansweredMessage = "";
          }

          // رسالة التنبيه
          const confirmed = await Swal.fire({
            title: this.$t("qbank.quiz.confirm_end_test"),
            html: `<p>${this.$t("qbank.quiz.confirm_end_message")}</p>
               <p style="color: #db1e1e;">${unansweredMessage}</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: this.$t("qbank.quiz.yes_complete"),
            cancelButtonText: this.$t("qbank.quiz.cancel"),
          });

          if (confirmed.isConfirmed) {
            //await this.submitAnswer();
            await this.completeQuiz();
          }
          return;
        }
        // إعادة تعيين الحالة، والانتقال للسؤال التالي
        this.answerRevealed = false;
        this.hasSelectedAnswer = false;
        this.questionNo += 1;
        this.updateUrl();
        this.fetchQuestion();
        this.destroySortable();
      }
      // لو مش في وضع "tutor"
    },
    async completeQuiz() {
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/complete`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.quiz.success"),
            text: this.$t("qbank.quiz.test_completed"),
            icon: "success",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          }).then(() => {
            const summaryUrl = this.subscriptionId
              ? `qbank-summary?qbank=${this.qbankId}&subscriptionId=${this.subscriptionId}`
              : `qbank-summary?qbank=${this.qbankId}`;
            window.location.replace(summaryUrl);
          });
        } else {
          Swal.fire({
            title: this.$t("qbank.quiz.error"),
            text: this.$t("qbank.quiz.error_ending_test"),
            icon: "error",
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          }).then(() => {
            const summaryUrl = this.subscriptionId
              ? `qbank-summary?qbank=${this.qbankId}&subscriptionId=${this.subscriptionId}`
              : `qbank-summary?qbank=${this.qbankId}`;
            window.location.replace(summaryUrl);
          });
        }
      } catch (error) {
        Swal.fire(
          this.$t("qbank.quiz.error"),
          this.$t("qbank.quiz.server_connection_error"),
          "error"
        );
      }
    },
    async completeQuizAndRedirect() {
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/complete`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
        if (response.ok) {
          Swal.fire({
            title: this.$t("qbank.quiz.success"),
            text: this.$t("qbank.quiz.test_completed"),
            icon: "success",
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false,
          }).then(() => {
            const summaryUrl = this.subscriptionId
              ? `qbank-summary?qbank=${this.qbankId}&subscriptionId=${this.subscriptionId}`
              : `qbank-summary?qbank=${this.qbankId}`;
            window.location.replace(summaryUrl);
          });
        } else {
          // Even if there's an error, redirect to summary page
          const summaryUrl = this.subscriptionId
            ? `qbank-summary?qbank=${this.qbankId}&subscriptionId=${this.subscriptionId}`
            : `qbank-summary?qbank=${this.qbankId}`;
          window.location.replace(summaryUrl);
        }
      } catch (error) {
        // Even if there's an error, redirect to summary page
        const summaryUrl = this.subscriptionId
          ? `qbank-summary?qbank=${this.qbankId}&subscriptionId=${this.subscriptionId}`
          : `qbank-summary?qbank=${this.qbankId}`;
        window.location.replace(summaryUrl);
      }
    },
    async confirmExitToMia() {
      // Show confirmation before exiting to Mia (no summary)
      const confirmed = await Swal.fire({
        title: this.$t("qbank.quiz.exit") || "Exit",
        text: this.$t("qbank.quiz.confirm_exit_message") || "Are you sure you want to exit? Your progress will be saved.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("qbank.quiz.yes_exit") || "Yes, Exit",
        cancelButtonText: this.$t("qbank.quiz.cancel"),
      });
      if (confirmed.isConfirmed) {
        this.$router.push({ name: "MiaV2RequestsList", query: { filter: "question_bank" } });
      }
    },
    exitToMia() {
      this.$router.push({ name: "MiaV2RequestsList", query: { filter: "question_bank" } });
    },
    async completeQuizForMia() {
      // Complete quiz and redirect to summary page with fromMia flag
      try {
        await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/complete`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
            },
          }
        );
      } catch (error) {
        console.error("Error completing quiz:", error);
      }
      // Redirect to summary page with fromMia flag
      const summaryUrl = `qbank-summary?qbank=${this.qbankId}&fromMia=1`;
      window.location.replace(summaryUrl);
    },
    goToQuestion(questionNum) {
      this.questionNo = questionNum;
      this.hasSelectedAnswer = false;
      // تحديث الـ URL بناءً على السؤال المختار
      this.$router.push({
        query: { ...this.$route.query, num: this.questionNo },
      });
      this.fetchQuestion();
    },
    previousQuestion() {
      if (this.questionNo > 1) {
        this.questionNo -= 1;
        this.hasSelectedAnswer = false;
        this.updateUrl();
        this.fetchQuestion();
        this.destroySortable();
      }
    },
    updateUrl() {
      this.$router.push({
        query: { ...this.$route.query, num: this.questionNo },
      });
    },
    async submitAnswer() {
      try {
        const formData = new FormData();

        // إعداد البيانات بناءً على نوع السؤال
        if (this.question.type === 3) {
          // سؤال نصي
          formData.append("answer", this.userAnswer);
        } else if (this.question.type === 1 || this.question.type === 2) {
          // اختيار من متعدد أو صح/خطأ
          formData.append("answer", this.userAnswer);
        } else if (this.question.type === 4) {
          // مطابقة
          this.question.matching_options.right.forEach((item, index) => {
            formData.append(
              `answer[${index}][left_option_id]`,
              this.question.matching_options.left[index].id
            );
            formData.append(`answer[${index}][right_option_id]`, item.id);
          });
        }

        // إرسال الطلب إلى الخادم
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/questions/${this.question.id}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`, // لا يتم تضمين Content-Type مع FormData
            },
            body: formData,
          }
        );

        if (!response.ok) {
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: "حدث خطأ أثناء إرسال الإجابة. حاول مرة أخرى.",
          });
          return;
        }

        /* Swal.fire({
                     icon: "success",
                     title: "تم الإرسال",
                     text: "تم إرسال الإجابة بنجاح!",
                 });*/
      } catch (error) {
        /*Swal.fire({
                    icon: "error",
                    title: "خطأ",
                    text: "حدث خطأ أثناء إرسال الإجابة. حاول مرة أخرى.",
                });*/
        this.answerRevealed = false;
        this.questionNo += 1;
        this.updateUrl();
        this.fetchQuestion();
        this.destroySortable();
        console.error("حدث خطأ أثناء إرسال الإجابة:", error);
      }
    },

    initSortable() {
      const sortableRight = this.$refs.sortableRight;
      if (!sortableRight) return;

      Sortable.create(sortableRight, {
        group: "matching",
        animation: 150,
        onEnd: (evt) => {
          const newOrder = Array.from(sortableRight.children).map((child) => {
            return this.question.matching_options.right.find(
              (item) => item.content === child.innerText.trim()
            );
          });

          this.question.matching_options.right = newOrder;
        },
      });
    },
    destroySortable() {
      const sortableRight = this.$refs.sortableRight;
      if (sortableRight && sortableRight._sortable) {
        sortableRight._sortable.destroy();
      }
    },
  },
  mounted() {
    this.fetchQuestion();
    this.renderMath();
  },
};
</script>

<style scoped>
/* ========== Container & Layout ========== */
.container {
  max-width: 1200px;
  padding: 1rem 0.5rem;
}

/* ========== Card Styling ========== */
.card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 8px 30px rgba(0, 196, 68, 0.1);
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  overflow: hidden;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease;
}

.card:hover {
  box-shadow: 0 12px 40px rgba(0, 196, 68, 0.15);
  transform: translateY(-2px);
}

.question-box {
  position: relative;
  padding: 1.5rem !important;
}

.question-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00c444 0%, #00e854 100%);
}

/* ========== Header Section ========== */
.btn-quiz-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 12px;
  margin-bottom: 1rem;
  border: 1px solid rgba(0, 196, 68, 0.1);
}

/* ========== Dark Mode Support ========== */
:root[data-theme="dark"] .card,
.dark .card,
[data-bs-theme="dark"] .card {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}

:root[data-theme="dark"] .question-box,
.dark .question-box,
[data-bs-theme="dark"] .question-box {
  background: #1e1e1e;
}

:root[data-theme="dark"] .btn-quiz-box,
.dark .btn-quiz-box,
[data-bs-theme="dark"] .btn-quiz-box {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%);
  border-color: rgba(0, 196, 68, 0.2);
}

:root[data-theme="dark"] .card-title,
.dark .card-title,
[data-bs-theme="dark"] .card-title {
  color: #e5e5e5;
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
}

:root[data-theme="dark"] .form-check,
.dark .form-check,
[data-bs-theme="dark"] .form-check {
  background: #2d2d2d;
  border-color: rgba(0, 196, 68, 0.2);
}

:root[data-theme="dark"] .form-check:hover,
.dark .form-check:hover,
[data-bs-theme="dark"] .form-check:hover {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%);
}

:root[data-theme="dark"] .form-check-label,
.dark .form-check-label,
[data-bs-theme="dark"] .form-check-label {
  color: #e5e5e5;
}

:root[data-theme="dark"] .list-group-item,
.dark .list-group-item,
[data-bs-theme="dark"] .list-group-item {
  background: #2d2d2d;
  border-color: rgba(0, 196, 68, 0.2);
  color: #e5e5e5;
}

:root[data-theme="dark"] .list-group-item:hover,
.dark .list-group-item:hover,
[data-bs-theme="dark"] .list-group-item:hover {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%);
}

:root[data-theme="dark"] .dropdown-menu,
.dark .dropdown-menu,
[data-bs-theme="dark"] .dropdown-menu {
  background: #2d2d2d;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

:root[data-theme="dark"] .dropdown-item,
.dark .dropdown-item,
[data-bs-theme="dark"] .dropdown-item {
  color: #e5e5e5;
}

:root[data-theme="dark"] .dropdown-item:hover,
.dark .dropdown-item:hover,
[data-bs-theme="dark"] .dropdown-item:hover {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%);
}

:root[data-theme="dark"] .timer-text,
.dark .timer-text,
[data-bs-theme="dark"] .timer-text {
  background: linear-gradient(135deg, #1a2e1a 0%, #1e3a1e 100%);
  border-color: rgba(0, 196, 68, 0.3);
}

:root[data-theme="dark"] .d-flex.justify-content-between.align-items-center,
.dark .d-flex.justify-content-between.align-items-center,
[data-bs-theme="dark"] .d-flex.justify-content-between.align-items-center {
  background: #2d2d2d;
}

:root[data-theme="dark"] .nav-buttons,
.dark .nav-buttons,
[data-bs-theme="dark"] .nav-buttons {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
}

:root[data-theme="dark"] textarea.form-control,
.dark textarea.form-control,
[data-bs-theme="dark"] textarea.form-control {
  background: #2d2d2d;
  border-color: rgba(0, 196, 68, 0.2);
  color: #e5e5e5;
}

:root[data-theme="dark"] .alert-dark,
.dark .alert-dark,
[data-bs-theme="dark"] .alert-dark {
  background: #3d3d3d;
  color: #e5e5e5;
}

:root[data-theme="dark"] .bg-light,
.dark .bg-light,
[data-bs-theme="dark"] .bg-light {
  background: #2d2d2d !important;
}

:root[data-theme="dark"] .text-center p,
.dark .text-center p,
[data-bs-theme="dark"] .text-center p {
  color: #a0a0a0;
}

:root[data-theme="dark"] pre,
.dark pre,
[data-bs-theme="dark"] pre {
  color: #e5e5e5;
  background: transparent;
}

/* ========== Question Header Bar ========== */
.question-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 14px;
  margin-bottom: 20px;
  gap: 12px;
  border: 1px solid #e2e8f0;
}

.question-progress-section {
  display: flex;
  align-items: center;
}

/* Question Number Button */
.question-number-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00c444 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}

.question-number-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 196, 68, 0.35);
}

.question-number-btn i {
  font-size: 1.1rem;
  color: white !important;
}

.question-num {
  font-size: 1.15rem;
  font-weight: 800;
  color: white !important;
}

.question-separator {
  margin: 0 2px;
  color: white !important;
}

.question-total {
  font-weight: 600;
  color: white !important;
}

/* Questions Dropdown */
.questions-dropdown {
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
  min-width: 180px;
}

.questions-dropdown .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.questions-dropdown .dropdown-item:hover {
  background: #f0fdf4;
}

.questions-dropdown .dropdown-item.current-question {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  font-weight: 700;
}

.question-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
}

.question-status-icon i {
  font-size: 0.9rem;
}

.answered-dot {
  color: #00c444;
  font-size: 0.5rem !important;
}

.question-item-num {
  font-weight: 600;
  color: #374151;
  font-size: 0.85rem;
  white-space: nowrap;
}

/* ========== Action Buttons ========== */
.question-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  border: 1.5px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  font-size: 1.15rem;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-btn:active {
  transform: translateY(0);
}

/* Bookmark Button */
.bookmark-btn {
  color: #f97316 !important;
  border-color: #e2e8f0;
}

.bookmark-btn i {
  color: #f97316 !important;
}

.bookmark-btn:hover {
  color: #ea580c !important;
  border-color: #fed7aa;
  background: #ffedd5;
}

.bookmark-btn:hover i {
  color: #ea580c !important;
}

.bookmark-btn.is-bookmarked {
  color: #ea580c !important;
  background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%);
  border-color: #fed7aa;
}

.bookmark-btn.is-bookmarked i {
  color: #ea580c !important;
}

.bookmark-btn.is-bookmarked:hover {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
}

.bookmark-btn.is-bookmarked:hover i {
  color: #ea580c !important;
}

/* AI Help Button */
.ai-help-btn {
  color: #8b5cf6;
  border-color: #e2e8f0;
}

.ai-help-btn:hover {
  color: #7c3aed;
  border-color: #c4b5fd;
  background: #f5f3ff;
}

/* End Quiz Button */
.end-quiz-btn {
  color: #000000;
  border-color: #e2e8f0;
  font-weight: 600;
}

.end-quiz-btn:hover {
  color: #000000;
  border-color: #cbd5e1;
  background: #f8fafc;
}

/* Exit Button (for Mia users) */
.exit-btn {
  width: auto;
  padding: 0 16px;
  color: #64748b;
  border-color: #e2e8f0;
  font-size: 0.875rem;
  font-weight: 500;
  gap: 6px;
}

.exit-btn:hover {
  color: #374151;
  border-color: #cbd5e1;
  background: #f8fafc;
}

/* More Options Button */
.more-btn {
  color: #64748b;
  border-color: #e2e8f0;
}

.more-btn:hover {
  color: #374151;
  border-color: #cbd5e1;
  background: #f8fafc;
}

.more-btn::after {
  display: none;
}

/* Dark Mode for Action Buttons */
[data-bs-theme="dark"] .question-header-bar,
.dark .question-header-bar {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #475569;
}

[data-bs-theme="dark"] .action-btn,
.dark .action-btn {
  background: #1e293b;
  border-color: #475569;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .bookmark-btn,
.dark .bookmark-btn {
  color: #fb923c !important;
}

[data-bs-theme="dark"] .bookmark-btn i,
.dark .bookmark-btn i {
  color: #fb923c !important;
}

[data-bs-theme="dark"] .bookmark-btn:hover,
.dark .bookmark-btn:hover {
  color: #f97316 !important;
  background: rgba(249, 115, 22, 0.15);
  border-color: rgba(249, 115, 22, 0.3);
}

[data-bs-theme="dark"] .bookmark-btn:hover i,
.dark .bookmark-btn:hover i {
  color: #f97316 !important;
}

[data-bs-theme="dark"] .bookmark-btn.is-bookmarked,
.dark .bookmark-btn.is-bookmarked {
  color: #f97316 !important;
  background: rgba(249, 115, 22, 0.2);
  border-color: rgba(249, 115, 22, 0.4);
}

[data-bs-theme="dark"] .bookmark-btn.is-bookmarked i,
.dark .bookmark-btn.is-bookmarked i {
  color: #f97316 !important;
}

/* ========== Navigation Bar ========== */
.navigation-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 14px;
  margin-top: 24px;
  border: 1px solid #e2e8f0;
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  min-width: 110px;
}

.nav-btn i {
  font-size: 1rem;
}

.nav-btn-prev {
  background: white;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.nav-btn-prev:hover {
  background: #f8fafc;
  color: #374151;
  border-color: #cbd5e1;
  transform: translateX(-3px);
}

.nav-btn-skip {
  background: white;
  color: #f59e0b;
  border: 1.5px solid #fcd34d;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.1);
}

.nav-btn-skip:hover {
  background: #fffbeb;
  color: #d97706;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.nav-btn-next {
  background: linear-gradient(135deg, #00c444 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.3);
}

.nav-btn-next i {
  color: white !important;
}

.nav-btn-next:hover {
  background: linear-gradient(135deg, #00d44e 0%, #00c444 100%);
  transform: translateX(3px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
}

/* RTL Support for Navigation */
[dir="rtl"] .nav-btn-prev:hover {
  transform: translateX(3px);
}

[dir="rtl"] .nav-btn-next:hover {
  transform: translateX(-3px);
}

/* End Exam Button */
.nav-btn-end-exam {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(220, 38, 38, 0.3);
}

.nav-btn-end-exam i {
  color: white !important;
}

.nav-btn-end-exam:hover {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
}

/* Button Fade Transition */
.btn-fade-enter-active,
.btn-fade-leave-active {
  transition: all 0.2s ease;
}

.btn-fade-enter-from,
.btn-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Dark Mode for Navigation */
[data-bs-theme="dark"] .navigation-bar,
.dark .navigation-bar {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #475569;
}

[data-bs-theme="dark"] .nav-btn-prev,
.dark .nav-btn-prev {
  background: #1e293b;
  color: #94a3b8;
  border-color: #475569;
}

[data-bs-theme="dark"] .nav-btn-prev:hover,
.dark .nav-btn-prev:hover {
  background: #334155;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .nav-btn-skip,
.dark .nav-btn-skip {
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

/* ========== Add Note Bar Button ========== */
.add-note-container {
  margin-bottom: 20px;
  text-align: left;
  direction: ltr;
}

.add-note-bar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #00c444 0%, #00a83b 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.25);
  transition: all 0.3s ease;
}

.add-note-bar-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.35);
  background: linear-gradient(135deg, #00d44e 0%, #00c444 100%);
}

.add-note-bar-btn:active {
  transform: translateY(0);
}

.add-note-bar-btn i {
  font-size: 1.1rem;
  color: white !important;
}

/* Dark Mode for Add Note Bar Button */
[data-bs-theme="dark"] .add-note-bar-btn,
.dark .add-note-bar-btn {
  background: linear-gradient(135deg, #00a83b 0%, #008f32 100%);
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.2);
}

/* Dark Mode for End Quiz Button */
[data-bs-theme="dark"] .end-quiz-btn,
.dark .end-quiz-btn {
  color: #ffffff;
  border-color: #475569;
}

[data-bs-theme="dark"] .end-quiz-btn:hover,
.dark .end-quiz-btn:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
  border-color: #64748b;
}

.timer-container {
  display: inline-flex;
  align-items: center;
}

.timer-text {
  font-size: 1.2rem;
  color: #00c444;
  font-weight: 700;
  padding: 8px 12px 8px 18px;
  background: rgba(0, 196, 68, 0.08);
  border-radius: 10px;
  border: 1.5px solid rgba(0, 196, 68, 0.25);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 6px rgba(0, 196, 68, 0.08);
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
}

.timer-text:hover {
  background: rgba(0, 196, 68, 0.12);
  border-color: rgba(0, 196, 68, 0.35);
  box-shadow: 0 3px 8px rgba(0, 196, 68, 0.12);
}

.timer-control-btn {
  background: rgba(0, 196, 68, 0.15);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  color: #00c444;
}

.timer-control-btn:hover {
  background: rgba(0, 196, 68, 0.25);
  transform: scale(1.05);
}

.timer-control-btn i {
  font-size: 0.75rem;
}

:root[data-theme="dark"] .timer-control-btn,
.dark .timer-control-btn,
[data-bs-theme="dark"] .timer-control-btn {
  background: rgba(0, 196, 68, 0.2);
  color: #00c444;
}

:root[data-theme="dark"] .timer-control-btn:hover,
.dark .timer-control-btn:hover,
[data-bs-theme="dark"] .timer-control-btn:hover {
  background: rgba(0, 196, 68, 0.3);
}

/* ========== Question Content ========== */
.card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.8;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 15px;
  border-left: 5px solid #00c444;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* ========== Answer Inputs ========== */
textarea.form-control {
  resize: none;
  border: 2px solid rgba(0, 196, 68, 0.2);
  border-radius: 15px;
  padding: 1.5rem;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

textarea.form-control:focus {
  border-color: #00c444;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.1);
  outline: none;
}

textarea.form-control:read-only {
  background: #f9fafb;
  cursor: not-allowed;
}

/* ========== Choice Options ========== */
.form-check {
  padding: 1.2rem 1.5rem;
  background: white;
  border: 2px solid rgba(0, 196, 68, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  margin-bottom: 0;
  position: relative;
}

.form-check:hover {
  border-color: #00c444;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.15);
}

.form-check-input {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border: 2px solid #00c444;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
  flex-shrink: 0;
}

/* RTL Layout (Arabic) */
[dir="rtl"] .form-check-input {
  margin-left: 20px;
  margin-right: 10px;
}

/* LTR Layout (English) */
[dir="ltr"] .form-check-input {
  margin-right: 20px;
  margin-left: 10px;
}

.form-check-input:checked {
  background-color: #00c444;
  border-color: #00c444;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.2);
}

.form-check-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-check-label {
  margin: 5px;
  font-size: 1.1rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  line-height: 1.6;
  flex: 1;
  display: inline-block;
}

/* ========== Matching Question ========== */
.list-group-item {
  cursor: move;
  border: 2px solid rgba(0, 196, 68, 0.15);
  border-radius: 10px;
  padding: 1rem 1.5rem;
  margin-bottom: 0.5rem;
  background: white;
  transition: all 0.3s ease;
  font-weight: 500;
}

.list-group-item:hover {
  border-color: #00c444;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.15);
}

.bg-success {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%) !important;
}

.bg-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%) !important;
}

/* ========== Alerts ========== */
.alert {
  border-radius: 12px;
  border: none;
  padding: 1.2rem 1.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.alert-success {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #166534;
}

.alert-danger {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
}

.alert-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

/* ========== Buttons ========== */
.btn {
  border-radius: 12px;
  padding: 12px 28px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.btn-secondary {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  color: white;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%);
}

.btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00e854 100%);
  color: white;
}

.btn-success:hover {
  background: linear-gradient(135deg, #00b03d 0%, #00d048 100%);
}

/* ========== Navigation Buttons ========== */
.d-flex.justify-content-between.mt-4 {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 15px;
  margin-top: 2rem;
  gap: 1rem;
}

/* ========== Loading & Empty States ========== */
.spinner-border {
  width: 4rem;
  height: 4rem;
  border-width: 0.4rem;
  border-color: #00c444;
  border-right-color: transparent;
}

.text-center p {
  font-size: 1.2rem;
  color: #6b7280;
  font-weight: 500;
  margin-top: 1rem;
}

/* ========== Dropdown Menu ========== */
.dropdown-menu {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.dropdown-item {
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  color: #374151;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #00c444;
  transform: translateX(5px);
}

.dropdown-item i {
  margin-right: 8px;
  color: #00c444;
}

.text-success {
  color: #00c444 !important;
  font-weight: 700;
}

/* ========== Animations ========== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Responsive Design ========== */

/* Large Tablets & Small Laptops */
@media (max-width: 992px) {
  .container {
    padding: 1rem;
  }

  .question-box {
    padding: 1.5rem !important;
  }

  .card-title {
    font-size: 1.3rem;
    padding: 1.2rem;
  }
}

/* Tablets */
@media (max-width: 768px) {
  .container {
    padding: 0.75rem 0.5rem;
  }

  .question-box {
    padding: 1.25rem !important;
    border-radius: 16px;
    margin: 0 -0.25rem;
  }

  .card-title {
    font-size: 1.15rem;
    padding: 1rem;
    line-height: 1.7;
    border-radius: 12px;
  }

  /* Question Header Bar - Tablet */
  .question-header-bar {
    padding: 10px 14px;
    gap: 10px;
  }

  .question-number-btn {
    padding: 8px 14px;
    font-size: 0.9rem;
  }

  .action-btn {
    width: 38px;
    height: 38px;
    font-size: 1.05rem;
  }

  /* Navigation Bar - Tablet */
  .navigation-bar {
    padding: 12px;
    gap: 10px;
  }

  .nav-btn {
    padding: 10px 16px;
    font-size: 0.9rem;
    min-width: 100px;
  }

  .form-check {
    padding: 1rem;
  }

  .form-check-label {
    font-size: 1rem;
  }

  .form-check-input {
    width: 22px;
    height: 22px;
    min-width: 22px;
  }

  .dropdown-menu {
    max-height: 60vh;
    overflow-y: auto;
  }
}

/* Mobile Phones */
@media (max-width: 576px) {
  .container {
    padding: 0.5rem 0.35rem;
  }

  .question-box {
    padding: 1rem !important;
    border-radius: 14px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  .card-title {
    font-size: 1.05rem;
    padding: 0.875rem;
    line-height: 1.65;
    margin-bottom: 1rem;
    border-left-width: 4px;
  }

  /* Question Header Bar - Mobile */
  .question-header-bar {
    padding: 10px 12px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .question-progress-section {
    flex: 1;
    min-width: 0;
  }

  .question-number-btn {
    padding: 8px 12px;
    font-size: 0.85rem;
    gap: 6px;
  }

  .question-number-btn i {
    font-size: 0.95rem;
  }

  .question-num {
    font-size: 1rem;
  }

  .question-actions {
    gap: 6px;
  }

  .action-btn {
    width: 36px;
    height: 36px;
    font-size: 1rem;
    border-radius: 8px;
  }

  /* Navigation Bar - Mobile */
  .navigation-bar {
    padding: 10px;
    gap: 8px;
    flex-wrap: wrap;
  }

  .nav-btn {
    padding: 10px 14px;
    font-size: 0.85rem;
    min-width: 0;
    flex: 1;
  }

  .nav-btn span {
    display: inline;
  }

  .nav-btn-skip {
    flex: 0 0 auto;
    order: 3;
    width: 100%;
    margin-top: 4px;
  }

  .nav-btn-prev {
    order: 1;
  }

  .nav-btn-next {
    order: 2;
  }

  /* Floating Note Button - Mobile */
  .floating-note-btn {
    bottom: 16px;
    right: 16px;
    padding: 12px 18px;
    font-size: 0.9rem;
    gap: 8px;
  }

  /* Add Note Bar Button - Mobile */
  .add-note-bar-btn {
    padding: 12px 16px;
    font-size: 0.9rem;
    margin-bottom: 16px;
  }

  .add-note-bar-btn i {
    font-size: 1.1rem;
  }

  .form-check {
    padding: 0.875rem;
    border-radius: 10px;
    margin-bottom: 0.5rem !important;
  }

  .form-check-label {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .form-check-input {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }

  .multi-choose,
  .true-false-question,
  .text-question {
    padding: 0.75rem !important;
    border-radius: 12px;
  }

  .true-false-question {
    width: 100% !important;
  }

  textarea.form-control {
    padding: 1rem;
    font-size: 1rem;
    min-height: 120px !important;
    border-radius: 12px;
  }

  .dropdown-menu {
    width: calc(100vw - 2rem);
    max-width: 300px;
    left: 50% !important;
    transform: translateX(-50%);
  }

  .dropdown-item {
    padding: 14px 16px;
    font-size: 0.95rem;
  }

  /* Explanation card mobile */
  .explanation-card {
    border-radius: 12px;
  }

  .explanation-header {
    padding: 12px 16px;
  }

  .explanation-content {
    padding: 16px;
  }

  .explanation-text,
  .explanation-text * {
    font-size: 16px !important;
    line-height: 1.7 !important;
  }

  /* Alert mobile */
  .alert {
    padding: 1rem;
    font-size: 1rem;
    border-radius: 10px;
  }

  /* Image responsive */
  .img-fluid {
    max-height: 280px !important;
    border-radius: 12px;
  }

  /* Matching question mobile */
  .list-group-item {
    padding: 0.875rem 1rem;
    font-size: 0.95rem;
  }
}

/* Small Mobile Phones */
@media (max-width: 375px) {
  .container {
    padding: 0.35rem 0.25rem;
  }

  .question-box {
    padding: 0.875rem !important;
  }

  .card-title {
    font-size: 1rem;
    padding: 0.75rem;
  }

  .question-header-bar {
    padding: 8px 10px;
  }

  .action-btn {
    width: 34px;
    height: 34px;
    font-size: 0.95rem;
  }

  .nav-btn {
    padding: 8px 10px;
    font-size: 0.8rem;
  }

  .form-check {
    padding: 0.75rem;
  }

  .form-check-label {
    font-size: 0.9rem;
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .form-check:active {
    transform: scale(0.98);
  }

  .btn:active {
    transform: scale(0.97);
  }

  .dropdown-item:active {
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  }
}

/* Landscape mode on mobile */
@media (max-width: 896px) and (orientation: landscape) {
  .question-box {
    padding: 1rem !important;
  }

  .card-title {
    font-size: 1rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .nav-buttons {
    padding: 0.75rem;
  }

  .teach-me-section {
    margin-bottom: 0.75rem;
  }
}

/* ========== Additional Enhancements ========== */
.btn-outline-primary {
  font-size: 1.2rem;
  padding: 10px 20px;
  transition: all 0.3s ease;
  background-color: rgba(0, 196, 68, 0.05);
  border: 2px solid #00c444;
  color: #00c444;
}

.btn-outline-primary:hover,
.btn-outline-primary.active {
  background: linear-gradient(135deg, #00c444 0%, #00e854 100%);
  color: #fff;
  border-color: #00c444;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.3);
}

.answered-circle {
  padding: 4px;
  width: 5px !important;
  height: 4px;
  display: inline-block;
  background-color: #00c444;
  border-radius: 50%;
}

.swal2-actions {
  font-family: "SF Arabic" !important;
}

.content-math {
  display: inline;
  margin: 7px;
  padding: 0;
  line-height: 1.4;
}

/* ========== Teach Me Button - Prominent Placement ========== */
.teach-me-section {
  display: flex;
  justify-content: center;
  align-items: center;
}

.teach-me-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00c444 0%, #00e854 100%);
  color: white;
  padding: 10px 24px;
  border: none;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.3);
  animation: pulse-glow 2s ease-in-out infinite;
}

.teach-me-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(0, 196, 68, 0.45);
  background: linear-gradient(135deg, #00e854 0%, #00c444 100%);
}

.teach-me-btn:active {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.35);
}

.teach-me-btn i {
  color: white;
  font-size: 1.1rem;
}

.teach-me-btn .fa-arrow-left {
  transition: transform 0.3s ease;
}

.teach-me-btn:hover .fa-arrow-left {
  transform: translateX(-5px);
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 8px 25px rgba(0, 196, 68, 0.3);
  }
  50% {
    box-shadow: 0 8px 35px rgba(0, 196, 68, 0.5);
  }
}

/* RTL Support for Teach Me Button */
[dir="rtl"] .teach-me-btn .fa-arrow-left {
  transform: scaleX(-1);
}

[dir="rtl"] .teach-me-btn:hover .fa-arrow-left {
  transform: scaleX(-1) translateX(-5px);
}

/* Responsive for Teach Me Button */
@media (max-width: 768px) {
  .teach-me-btn {
    padding: 12px 24px;
    font-size: 0.95rem;
    width: 100%;
    justify-content: center;
  }
}

/* ========== Add Private Notes Button ========== */
.add-private-notes-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 0 10px;
}

.btn-add-private-notes {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, #00c444 0%, #00a83b 100%);
  color: white;
  padding: 12px 28px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
  width: 100%;
  max-width: 350px;
}

.btn-add-private-notes:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.4);
  background: linear-gradient(135deg, #00d44e 0%, #00c444 100%);
}

.btn-add-private-notes:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
}

.btn-add-private-notes i {
  color: white !important;
  font-size: 1.1rem;
}

/* ========== Question Details Modal Styles ========== */
.question-details-modal {
  border-radius: 16px;
  overflow: hidden;
}

.question-details-modal .modal-header {
  background: linear-gradient(135deg, #00c444 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 16px 20px;
}

.question-details-modal .modal-title {
  font-weight: 600;
  display: flex;
  align-items: center;
}

.question-details-modal .modal-title i {
  color: white !important;
}

.question-details-modal .btn-close {
  filter: brightness(0) invert(1);
}

.question-details-modal .modal-body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #07c232 #e2e8f0;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-icon i {
  color: white !important;
  font-size: 14px;
}

.detail-icon.lesson {
  background: #3b82f6;
}
.detail-icon.category {
  background: #8b5cf6;
}
.detail-icon.source {
  background: #06b6d4;
}
.detail-icon.difficulty {
  background: #f59e0b;
}
.detail-icon.time {
  background: #10b981;
}
.detail-icon.percentage {
  background: #00c444;
}
.detail-icon.tags {
  background: #ec4899;
}
.detail-icon.type {
  background: #6366f1;
}

.detail-icon.reports {
  background: #f97316;
}

.detail-icon.notes {
  background: #eab308;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.detail-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 600;
}

.detail-value-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-view-details {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #00c444;
  background: #e8fff1;
  border: 1px solid #00c444;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-view-details:hover {
  background: #00c444;
  color: white;
}

.btn-view-details i {
  font-size: 0.7rem;
}

.percentage-value {
  color: #00c444;
}

.difficulty-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.difficulty-easy {
  background: #dcfce7;
  color: #16a34a;
}
.difficulty-medium {
  background: #fef3c7;
  color: #d97706;
}
.difficulty-hard {
  background: #fee2e2;
  color: #dc2626;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.tag-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.question-details-modal .modal-footer {
  border: none;
  padding: 14px 20px;
  background: #f8fafc;
}

.question-details-modal .btn-secondary {
  background: #e2e8f0;
  color: #64748b;
  border: none;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 600;
}

.question-details-modal .btn-secondary:hover {
  background: #cbd5e1;
  color: #475569;
}

/* Dark mode for Question Details Modal */
[data-bs-theme="dark"] .question-details-modal .modal-content,
.dark .question-details-modal .modal-content {
  background: #1e293b;
}

[data-bs-theme="dark"] .detail-row,
.dark .detail-row {
  background: #334155;
}

[data-bs-theme="dark"] .detail-value,
.dark .detail-value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .question-details-modal .modal-footer,
.dark .question-details-modal .modal-footer {
  background: #1e293b;
}

[data-bs-theme="dark"] .question-details-modal .btn-secondary,
.dark .question-details-modal .btn-secondary {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .tag-badge,
.dark .tag-badge {
  background: #1e3a5f;
  color: #7dd3fc;
}

/* ========== Beautiful Explanation Card Styles ========== */
.explanation-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdfa 100%);
  border: 1px solid #86efac;
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(34, 197, 94, 0.1);
}

.explanation-header {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.explanation-icon {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff !important;
  font-size: 1.1rem;
}

.explanation-icon i {
  color: #fff !important;
}

.explanation-title {
  margin: 0;
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.3px;
}

.explanation-content {
  padding: 20px;
}

.explanation-text {
  font-family: "SF Arabic", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif !important;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  color: #166534;
  font-size: 20px !important;
  line-height: 1.8;
}

.explanation-text * {
  font-size: 20px !important;
  line-height: 1.8 !important;
}

.explanation-image {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.explanation-file {
  padding: 0 20px 20px 20px;
}

.explanation-file-link {
  display: inline-flex;
  align-items: center;
  padding: 12px 20px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.explanation-file-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
  color: #fff;
}

/* Dark mode for Explanation Card */
[data-bs-theme="dark"] .explanation-card,
.dark .explanation-card {
  background: linear-gradient(135deg, #14532d 0%, #166534 50%, #15803d 100%);
  border-color: #22c55e;
}

[data-bs-theme="dark"] .explanation-text,
.dark .explanation-text {
  color: #bbf7d0;
}

[data-bs-theme="dark"] .explanation-header,
.dark .explanation-header {
  background: linear-gradient(135deg, #15803d 0%, #166534 100%);
}
</style>

<style>
/* Question Details Modal Scrollbar - Unscoped for proper application */
.question-details-modal .modal-body::-webkit-scrollbar {
  width: 8px;
}

.question-details-modal .modal-body::-webkit-scrollbar-track {
  background: #e2e8f0;
  border-radius: 4px;
}

.question-details-modal .modal-body::-webkit-scrollbar-thumb {
  background: #07c232;
  border-radius: 4px;
}

.question-details-modal .modal-body::-webkit-scrollbar-thumb:hover {
  background: #05a627;
}

[data-bs-theme="dark"]
  .question-details-modal
  .modal-body::-webkit-scrollbar-track,
.dark .question-details-modal .modal-body::-webkit-scrollbar-track {
  background: #374151;
}
</style>
